import { isPlatformBrowser } from '@angular/common';
import {
  Inject,
  Injectable,
  PLATFORM_ID,
  makeStateKey,
  TransferState,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import {
  BookedMeetingInfo,
  BookingSource,
  Calendar,
  Contact,
  durationStringToMinutes,
  Group,
  GuestService,
  Host,
  MeetingForm,
  MeetingLocationType,
  NotificationType,
  Service,
  TeamEventMeetingType,
  TimeSpan,
  TimeZone,
} from '@vendasta/meetings';
import {
  BatchBookingTargetInterface,
  BatchEventTypeTargetInterface,
  EventTypeDateRangeInterface,
  FeatureFlagFieldType,
  GetGroupResponseInterface,
  GetServiceResponseInterface,
  GetServiceV2Response,
  GuestGetBookedMeetingResponseV2,
  HostUserInterface,
  ServiceEventType,
  ServiceGroup,
} from '@vendasta/meetings';
import { BehaviorSubject, NEVER, Observable } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  switchMap,
  switchMapTo,
  tap,
} from 'rxjs/operators';
import { TimeSlot, TimezoneRegionAndZone } from './bookme/bookme';
import { getDaniMarioForm, MeetingType, MeetingTypeForm } from './interface';
import {
  clearUniqueRequestId,
  getOrCreateUniqueRequestId,
  migrateUniqueRequestId,
  resolveBookingSource,
} from './booking-request-id';
import {
  ANY_AVAILABLE_PROVIDER,
  getTimezone,
  getTimezoneOffsetFromTimezone,
  getZoneFromTimezone,
  trimTimeRangeToFromNow,
} from './util';

export type BookingStep = 'scheduling' | 'provider' | 'datetime' | 'confirm';

export type Errors =
  // Host/calendar errors
  | 'no-host'
  | 'error-host'
  // Booking errors
  | 'conflict-booking'
  | 'error-404'
  | 'error-lookup'
  // Event type errors
  | 'error-meeting-type'
  | 'slug-mismatch-error'
  | 'meeting-type-deleted'
  | 'meeting-type-expired'
  | 'meeting-host-not-connected'
  // Meeting errors (cancel/info)
  | 'meeting-already-cancelled'
  | 'meeting-not-found'
  | 'meeting-error'
  // Meeting errors (reschedule)
  | 'meeting-cancelled-cannot-reschedule'
  | 'meeting-reschedule-not-found'
  | 'meeting-reschedule-error'
  // Shared video errors
  | 'shared-video-not-found'
  | 'shared-video-error'
  // Session errors
  | 'error-booking-session'
  | 'session-not-found'
  | 'session-deleted'
  | 'session-expired'
  | 'session-max-attendee-count'
  | 'session-already-booked'
  | 'session-cannot-cancel-meeting-started'
  | 'session-already-cancelled'
  | 'session-not-active'
  | 'session-error';

type CompletedMeetingAction = '' | 'cancel' | 'reschedule' | 'book';

export interface CustomerInfoDraft {
  formValue: Record<string, unknown>;
  guestEmails: string[];
  showGuestEmails: boolean;
  customAnswers: Record<string, unknown>;
}

interface BookMeNowState {
  calendarId: string;
  eventTypeId: string;
  meetingTypes: MeetingType[];
  meetingType: MeetingType;
  timeSlots: TimeSlot[];
  calendar: Calendar;
  selectedDate: Date;
  selectedTimeSlot: TimeSlot;
  originalMeetingTimeSlot: TimeSlot;
  timeRange: Pick<TimeSlot, 'start' | 'end'>;
  timeZone: string;
  timeZoneChanged: boolean;
  errorDisplay: Errors;
  meetingToRescheduleOrCancelId: string;
  metadata: { [key: string]: string };
  completedMeetingAction: CompletedMeetingAction;
  completedMeetingMessage: string;
  completedMeetingHost: Host | null;
  meetingLocation: string;
  newMeetingLocation: string;
  isTimeSlotChanged: boolean;
  isLocationChanged: boolean;
  locationType: MeetingLocationType;
  displayName: string;
  locationGuideLine: string;
  selectedHost: string;
  isLoadingTimeSlots: boolean;
  teamEventMeetingType: TeamEventMeetingType;
  guestBookedMeetingV2: GuestGetBookedMeetingResponseV2;
}

const defaultState: BookMeNowState = Object.freeze({
  calendarId: '',
  eventTypeId: '',
  calendar: null,
  timeSlots: null,
  meetingTypes: [],
  meetingType: {} as MeetingType,
  timeRange: null,
  selectedDate: null,
  selectedTimeSlot: null,
  originalMeetingTimeSlot: null,
  timeZone: getTimezone(),
  timeZoneChanged: false,
  errorDisplay: undefined,
  metadata: {},
  meetingToRescheduleOrCancelId: '',
  completedMeetingAction: '',
  completedMeetingMessage: '',
  completedMeetingHost: null,
  meetingLocation: '',
  isTimeSlotChanged: false,
  isLocationChanged: false,
  newMeetingLocation: '',
  locationType: MeetingLocationType.VIDEO,
  locationGuideLine: '',
  hostUsers: [],
  selectedHost: '',
  isLoadingTimeSlots: false,
  teamEventMeetingType: TeamEventMeetingType.ROUND_ROBIN,
  displayName: '',
  guestBookedMeetingV2: null,
});

const bookmenowStateKey = makeStateKey<BookMeNowState>('BOOK_ME_NOW_STATE_KEY');

@Injectable({
  providedIn: 'root',
})
export class BookMeNowStoreService {
  private readonly state$$: BehaviorSubject<BookMeNowState> =
    new BehaviorSubject(
      // overwrite timezone to prevent showing UTC in the ui
      {
        ...this.transferStateService.get(bookmenowStateKey, defaultState),
        timeZone: getTimezone(),
        timeZoneChanged: false,
      }
    );
  private readonly state$: Observable<BookMeNowState>;

  readonly calendar$: Observable<Calendar>;
  readonly completedMeetingHost$: Observable<Host | null>;
  readonly timeSlot$: Observable<TimeSlot>;
  readonly timezone$: Observable<string>;
  readonly timezoneOffset$: Observable<string>;
  readonly errorDisplay$: Observable<Errors>;
  readonly meetingTypes$: Observable<MeetingType[]>;
  readonly meetingTypeId$: Observable<string>;
  readonly meetingTypeDuration$: Observable<number>;
  readonly meetingTypeDescription$: Observable<string>;
  readonly meetingTypeForm$: Observable<MeetingTypeForm>;
  readonly rescheduled$: Observable<boolean>;
  readonly canceled$: Observable<boolean>;
  readonly confirmationMessage$: Observable<string>;
  readonly calendarId$: Observable<string>;
  readonly timeSlots$: Observable<TimeSlot[]>;
  readonly inPersonLocation$: Observable<string>;
  readonly meetingLocation$: Observable<string>;
  readonly newMeetingLocation$: Observable<string>;
  readonly locationType$: Observable<MeetingLocationType>;
  readonly teamEventMeetingType$: Observable<TeamEventMeetingType>;
  readonly locationGuideLine$: Observable<string>;
  private readonly initialPathKey = 'bookingInitialPath';
  readonly teamMemberList$: Observable<HostUserInterface[]>;
  readonly isClientChoiceMeeting$: Observable<boolean>;
  readonly isLoadingTimeSlots$: Observable<boolean>;
  readonly displayName$: Observable<string>;
  readonly originalMeetingTimeSlot$: Observable<TimeSlot>;
  readonly guestBookedMeetingV2$: Observable<GuestGetBookedMeetingResponseV2>;
  private _businessDisplayLogoUrl = new BehaviorSubject<string | null>(null);
  readonly businessDisplayLogoUrl$ =
    this._businessDisplayLogoUrl.asObservable();

  private _serviceLocation = new BehaviorSubject<string>('');
  readonly serviceLocation$ = this._serviceLocation.asObservable();

  /** Selected events for the new scheduling flow (shared between scheduling and provider pages) */
  private readonly _selectedBookingEvents$$ = new BehaviorSubject<
    ServiceEventType[]
  >([]);
  readonly selectedBookingEvents$ =
    this._selectedBookingEvents$$.asObservable();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setSelectedBookingEvents(events: any[]): void {
    this._selectedBookingEvents$$.next(events as ServiceEventType[]);
  }

  getSelectedBookingEvents(): ServiceEventType[] {
    return this._selectedBookingEvents$$.getValue();
  }

  /** Booking flow step management */
  private readonly _bookingStep$$ = new BehaviorSubject<BookingStep>(
    'scheduling'
  );
  readonly bookingStep$ = this._bookingStep$$.asObservable();

  setBookingStep(step: BookingStep): void {
    this._bookingStep$$.next(step);
  }

  getBookingStep(): BookingStep {
    return this._bookingStep$$.getValue();
  }

  resetBookingStep(): void {
    this._bookingStep$$.next('scheduling');
  }

  /** Whether the provider selection step should be shown in the booking flow */
  private readonly _shouldShowProviderStep$$ = new BehaviorSubject<boolean>(false);
  readonly shouldShowProviderStep$ = this._shouldShowProviderStep$$.asObservable();

  private readonly _providerStepDetermined$$ = new BehaviorSubject<boolean>(false);
  readonly providerStepDetermined$ = this._providerStepDetermined$$.asObservable();

  private readonly _providerStepSkipped$$ = new BehaviorSubject<boolean>(false);
  readonly providerStepSkipped$ = this._providerStepSkipped$$.asObservable();

  setShouldShowProviderStep(value: boolean): void {
    this._shouldShowProviderStep$$.next(value);
    this._providerStepDetermined$$.next(true);
  }

  setProviderStepSkipped(value: boolean): void {
    this._providerStepSkipped$$.next(value);
  }

  getProviderStepSkipped(): boolean {
    return this._providerStepSkipped$$.getValue();
  }

  getShouldShowProviderStep(): boolean {
    return this._shouldShowProviderStep$$.getValue();
  }

  /** Reschedule mode state */
  private _isRescheduleMode = false;
  private readonly _rescheduleTargets$$ = new BehaviorSubject<
    BatchEventTypeTargetInterface[]
  >([]);
  readonly rescheduleTargets$ = this._rescheduleTargets$$.asObservable();

  setRescheduleMode(value: boolean): void {
    this._isRescheduleMode = value;
    if (!value) {
      this._rescheduleTargets$$.next([]);
    }
  }

  getIsRescheduleMode(): boolean {
    return this._isRescheduleMode;
  }

  setRescheduleTargets(targets: BatchEventTypeTargetInterface[]): void {
    this._rescheduleTargets$$.next(targets);
  }

  private _customerInfoDraft: CustomerInfoDraft | null = null;

  setCustomerInfoDraft(draft: CustomerInfoDraft): void {
    this._customerInfoDraft = draft;
  }

  getCustomerInfoDraft(): CustomerInfoDraft | null {
    return this._customerInfoDraft;
  }

  clearCustomerInfoDraft(): void {
    this._customerInfoDraft = null;
  }

  private _bookingOriginId = '';

  setBookingOriginId(id: string): void {
    this._bookingOriginId = id || '';
  }

  getBookingOriginId(): string {
    return this._bookingOriginId;
  }

  /** Provider name mapping (eventTypeId → providerName) for cart display */
  private _eventProviderMap = new Map<string, string>();

  setEventProviderMap(map: Map<string, string>): void {
    this._eventProviderMap = new Map(map);
  }

  getProviderName(eventTypeId: string): string {
    return this._eventProviderMap.get(eventTypeId) || 'Any available';
  }

  getProviderNameForEvent(event: ServiceEventType): string {
    const stored = this.getProviderName(event.id);
    if (stored !== ANY_AVAILABLE_PROVIDER) return stored;
    const type = event.eventType ?? TeamEventMeetingType.ROUND_ROBIN;
    const allHosts = event.hostUser || [];
    const hasConnected = allHosts.some((h: HostUserInterface) => h.isConnected);
    const hosts = hasConnected
      ? allHosts.filter((h: HostUserInterface) => h.isConnected)
      : allHosts;

    if (type === TeamEventMeetingType.MULTI_HOST) {
      return hosts.map((h: HostUserInterface) => h.displayName).filter(Boolean).join(', ') || 'Any available';
    }
    if (hosts.length === 1) {
      return hosts[0].displayName || 'Any available';
    }
    return 'Any available';
  }

  getEventProviderMapRaw(): Map<string, string> {
    return new Map(this._eventProviderMap);
  }

  /** Provider selection mode ('all' | 'per-service') */
  private _providerSelectionMode: 'all' | 'per-service' = 'all';

  setProviderSelectionMode(mode: 'all' | 'per-service'): void {
    this._providerSelectionMode = mode;
  }

  getProviderSelectionMode(): 'all' | 'per-service' {
    return this._providerSelectionMode;
  }

  /** Per-event host ID mapping (eventTypeId → hostUserId) for restoring provider selections */
  private _eventHostMap = new Map<string, string>();

  setEventHostMap(map: Map<string, string>): void {
    this._eventHostMap = new Map(map);
  }

  getEventHostMap(): Map<string, string> {
    return new Map(this._eventHostMap);
  }

  private _eventSourceGroupMap = new Map<string, string>();

  setEventSourceGroupMap(map: Map<string, string>): void {
    this._eventSourceGroupMap = new Map(map);
  }

  getEventSourceGroupMap(): Map<string, string> {
    return new Map(this._eventSourceGroupMap);
  }
  readonly dateRange$: Observable<EventTypeDateRangeInterface>;
  readonly notificationType$: Observable<NotificationType>;

  constructor(
    private readonly guestService: GuestService,
    private readonly transferStateService: TransferState,
    @Inject(PLATFORM_ID) private platformId: object,
    private readonly meta: Meta
  ) {
    this.state$ = this.state$$.asObservable().pipe(
      distinctUntilChanged(),
      tap((state) => this.transferStateService.set(bookmenowStateKey, state)),
      shareReplay(1)
    );
    this.calendarId$ = this.state$.pipe(map((state) => state.calendarId));
    this.calendar$ = this.state$.pipe(map((state) => state.calendar));
    this.timeSlot$ = this.state$.pipe(
      map((state) => {
        if (!state.selectedTimeSlot) {
          return null;
        }
        return {
          ...state.selectedTimeSlot,
          timezone:
            state.selectedTimeSlot.timezone ||
            this.createTimezone(state.selectedTimeSlot.start),
        };
      })
    );
    this.timezoneOffset$ = this.state$.pipe(
      map((state) =>
        getTimezoneOffsetFromTimezone({
          fromStart: state.selectedDate,
          timeZone: state.timeZone,
        })
      )
    );
    this.errorDisplay$ = this.state$.pipe(
      map((state) => state.errorDisplay || undefined)
    );
    this.meetingTypes$ = this.state$.pipe(map((state) => state.meetingTypes));
    this.meetingTypeId$ = this.state$.pipe(
      map((state) => (state.meetingType ? state.meetingType.id : '')),
      distinctUntilChanged()
    );

    this.timezone$ = this.state$.pipe(map((state) => state.timeZone));
    this.rescheduled$ = this.state$.pipe(
      map((state) => state.completedMeetingAction === 'reschedule')
    );
    this.canceled$ = this.state$.pipe(
      map((state) => state.completedMeetingAction === 'cancel')
    );
    this.confirmationMessage$ = this.state$.pipe(
      map((state) => state.completedMeetingMessage)
    );
    this.timeSlots$ = this.state$.pipe(map((state) => state.timeSlots));

    this.teamMemberList$ = this.state$.pipe(
      map((state) => state.meetingType.hostUsers)
    );

    this.isClientChoiceMeeting$ = this.state$.pipe(
      map(
        (state) =>
          state.meetingType?.meetingType === TeamEventMeetingType.CLIENT_CHOICE
      )
    );

    this.inPersonLocation$ = this.state$.pipe(
      map((state) => state.meetingType.location)
    );

    this.meetingLocation$ = this.state$.pipe(
      map((state) => state.meetingLocation)
    );

    this.newMeetingLocation$ = this.state$.pipe(
      map((state) => state.newMeetingLocation)
    );
    this.dateRange$ = this.state$.pipe(
      map((state) => state.meetingType?.dateRange),
      distinctUntilChanged()
    );

    this.notificationType$ = this.state$.pipe(
      map(
        (state) => state.meetingType?.notificationType ?? NotificationType.EMAIL
      )
    );

    this.locationType$ = this.state$.pipe(map((state) => state.locationType));

    this.displayName$ = this.state$.pipe(map((state) => state.displayName));

    this.teamEventMeetingType$ = this.state$.pipe(
      map((state) => state.teamEventMeetingType)
    );

    this.locationGuideLine$ = this.state$.pipe(
      map((state) => state.locationGuideLine)
    );

    this.meetingTypeDuration$ = this.state$.pipe(
      map((state) =>
        state.meetingType
          ? durationStringToMinutes({
              duration: state.meetingType.duration,
              round: true,
            })
          : undefined
      )
    );
    this.meetingTypeDescription$ = this.state$.pipe(
      map((state) => (state.meetingType ? state.meetingType.description : ''))
    );
    this.completedMeetingHost$ = this.state$$.pipe(
      map((state) => state.completedMeetingHost)
    );

    this.meetingTypeForm$ = this.state$$.pipe(
      map((state) => state?.meetingType?.form)
    );

    this.calendar$.subscribe((cal) => {
      if (cal?.displayLogoUrl) {
        this.meta.updateTag({
          property: 'og:image',
          content: cal.displayLogoUrl,
        });
      }
    });

    this.isLoadingTimeSlots$ = this.state$$.pipe(
      map((state) => state.isLoadingTimeSlots),
      distinctUntilChanged()
    );

    this.originalMeetingTimeSlot$ = this.state$.pipe(
      map((state) => {
        if (!state.originalMeetingTimeSlot) {
          return null;
        }
        return {
          ...state.originalMeetingTimeSlot,
          timezone:
            state.originalMeetingTimeSlot.timezone ||
            this.createTimezone(state.originalMeetingTimeSlot.start),
        };
      })
    );

    this.guestBookedMeetingV2$ = this.state$.pipe(
      map((state) => state.guestBookedMeetingV2)
    );
  }

  public handleMeetingTypeError(err: {
    error?: { message?: string };
    message?: string;
  }): void {
    const message = (err?.error?.message || err?.message || '').toLowerCase();
    if (message.includes('no longer exists')) {
      this.setErrorDisplay('meeting-type-deleted');
    } else if (message.includes('not found')) {
      this.setErrorDisplay('error-404');
    } else if (message.includes('expired')) {
      this.setErrorDisplay('meeting-type-expired');
    } else if (
      message.includes('host calendar/meeting app link is not connected')
    ) {
      this.setErrorDisplay('meeting-host-not-connected');
    } else {
      this.setErrorDisplay('error-meeting-type');
    }
  }

  public handleMeetingError(
    err: { error?: { message?: string }; message?: string; status?: number },
    context: 'cancel' | 'reschedule' | 'info'
  ): void {
    const message = (err?.error?.message || err?.message || '').toLowerCase();
    const status = err?.status;

    if (
      message.includes('already cancelled') ||
      message.includes('cancelled meeting')
    ) {
      this.setErrorDisplay(
        context === 'reschedule'
          ? 'meeting-cancelled-cannot-reschedule'
          : 'meeting-already-cancelled'
      );
    } else if (status === 404 || status === 403) {
      this.setErrorDisplay(
        context === 'reschedule'
          ? 'meeting-reschedule-not-found'
          : 'meeting-not-found'
      );
    } else {
      this.setErrorDisplay(
        context === 'reschedule' ? 'meeting-reschedule-error' : 'meeting-error'
      );
    }
  }

  loadCalendar(calendarId: string): Observable<Calendar> {
    return this.guestService.getCalendar({ calendarId }).pipe(
      tap((calendar: Calendar) =>
        this.state$$.next({
          ...this.state$$.getValue(),
          calendar,
          calendarId: calendar.id,
        })
      )
    );
  }

  loadMeetingType(meetingTypeSlug: string): Observable<MeetingType> {
    return this.calendarId$.pipe(
      filter<string>(Boolean),
      distinctUntilChanged(),
      switchMap((calendarId) => {
        return this.guestService.getMeetingType({
          calendarSlug: calendarId,
          meetingTypeSlug,
          uniqueRequestId: getOrCreateUniqueRequestId(meetingTypeSlug),
          bookingSource: resolveBookingSource(),
        });
      }),
      map((meetingType) =>
        ifDaniMarioEnsureCustomFields(meetingType as MeetingType)
      ),
      tap((meetingType) => {
        migrateUniqueRequestId(meetingTypeSlug, meetingType.id);
        this.state$$.next({ ...this.state$$.getValue(), meetingType });
      })
    );
  }

  loadMeetingTypeForGivenCalendarIdAndSlug(
    calendarId: string,
    meetingTypeSlug: string
  ): Observable<MeetingType> {
    return this.guestService
      .getMeetingType({
        calendarSlug: calendarId,
        meetingTypeSlug,
        uniqueRequestId: getOrCreateUniqueRequestId(meetingTypeSlug),
        bookingSource: resolveBookingSource(),
      })
      .pipe(
        map((meetingType) =>
          ifDaniMarioEnsureCustomFields(meetingType as MeetingType)
        ),
        tap((meetingType) => {
          migrateUniqueRequestId(meetingTypeSlug, meetingType.id);
          this.state$$.next({ ...this.state$$.getValue(), meetingType });
          this.state$$.next({
            ...this.state$$.getValue(),
            teamEventMeetingType: meetingType.meetingType,
          });
        })
      );
  }

  loadMeetingTypes(req: {
    calendarId: string;
    calendarSlug: string;
  }): Observable<MeetingType[]> {
    return this.guestService
      .listMeetingTypes({
        hostId: req.calendarId,
        calendarSlug: req.calendarSlug,
        uniqueRequestId: getOrCreateUniqueRequestId(req.calendarId),
        bookingSource: resolveBookingSource(),
      })
      .pipe(
        tap((response) => {
          this._businessDisplayLogoUrl.next(
            response.businessDisplayLogoUrl || ''
          );
        }),
        map((response) =>
          (response.meetingTypes || []).map((mt) =>
            ifDaniMarioEnsureCustomFields(mt as MeetingType)
          )
        ),
        tap((meetingTypes) => {
          this.state$$.next({
            ...this.state$$.getValue(),
            meetingTypes: meetingTypes as MeetingType[],
          });
        })
      ) as Observable<MeetingType[]>;
  }

  loadGroup(req: { groupId: string; groupSlug: string }): Observable<Group> {
    return this.guestService
      .getGroup({
        groupId: req.groupId,
        slug: req.groupSlug,
        uniqueRequestId: getOrCreateUniqueRequestId(req.groupId),
        bookingSource: resolveBookingSource(),
      })
      .pipe(
        map((response: GetGroupResponseInterface) => {
          const group: Group = {
            id: response.group.id,
            name: response.group.name,
            description: response.group.description,
            slug: response.group.slug,
            associations: response.group.associations,
            isPinned: response.group.isPinned,
            isBatchBookingEnabled: response.group.isBatchBookingEnabled,
          };
          this._businessDisplayLogoUrl.next(response.businessDisplayLogoUrl);
          return group;
        })
      );
  }

  getGuestBookedMeetingV2(req: {
    id: string;
  }): Observable<GuestGetBookedMeetingResponseV2> {
    return this.guestService.getGuestBookedMeetingV2(req);
  }

  loadService(req: {
    serviceId: string;
    serviceSlug: string;
  }): Observable<Service> {
    return this.guestService
      .getService({
        serviceId: req.serviceId,
        slug: req.serviceSlug,
        uniqueRequestId: getOrCreateUniqueRequestId(req.serviceId),
        bookingSource: resolveBookingSource(),
      })
      .pipe(
        map((response: GetServiceResponseInterface) => {
          const service: Service = {
            id: response.service.id,
            name: response.service.name,
            description: response.service.description,
            slug: response.service.slug,
            associations: response.service.associations,
            isPinned: response.service.isPinned,
            isBatchBookingEnabled: response.service.isBatchBookingEnabled,
          };
          this._businessDisplayLogoUrl.next(response.businessDisplayLogoUrl);
          return service;
        })
      );
  }

  loadServiceV2(req: { serviceId: string }): Observable<GetServiceV2Response> {
    this._selectedBookingEvents$$.next([]);
    this._eventSourceGroupMap.clear();

    return this.guestService
      .getServiceV2({
        serviceId: req.serviceId,
        uniqueRequestId: getOrCreateUniqueRequestId(req.serviceId),
        bookingSource: resolveBookingSource(),
      })
      .pipe(
      map(
        (response: GetServiceV2Response & { events?: ServiceEventType[] }) => {
          // Workaround: SDK fromProto expects 'eventTypes' but API returns 'events'
          // Object.assign in fromProto copies 'events' as a raw field
          // Assign events → eventTypes directly until the SDK is updated
          const mapped = response;
          if (!mapped.eventTypes?.length && response.events?.length) {
            mapped.eventTypes = response.events;
          }
          if (mapped.groups?.length) {
            mapped.groups.forEach(
              (group: ServiceGroup & { events?: ServiceEventType[] }) => {
                if (!group.eventTypes?.length && group.events?.length) {
                  group.eventTypes = group.events;
                }
              }
            );
          }
          return mapped;
        }
      ),
      tap((response) => {
        this._businessDisplayLogoUrl.next(response.businessDisplayLogoUrl || '');
        this._extractServiceLocation(response);
      })
    );
  }

  loadAvailableTimeSlots(request?: {
    calendarId?: string;
    meetingTypeId?: string;
    timeSpan?: TimeSpan;
    timeZone?: TimeZone;
    userId?: string;
  }): Observable<TimeSpan[]> {
    // This call can take a very long time and is not accurate because the server does not know the correct timezone
    if (this.runningOnServer()) {
      return NEVER;
    }

    this.state$$.next({
      ...this.state$$.getValue(),
      isLoadingTimeSlots: true,
    });
    const req = request || {};
    return this.state$.pipe(
      filter<BookMeNowState>((state) => {
        return [
          req.timeZone || state.timeZone,
          req.calendarId || state.calendarId,
          req.meetingTypeId || (state.meetingType && state.meetingType.id),
        ].every(Boolean);
      }),
      first(),
      switchMap((state) => {
        return this.guestService.listAvailableTimeSlots({
          ...req,
          hostId: req.calendarId || state.calendarId,
          meetingTypeId:
            req.meetingTypeId ||
            (state.meetingType ? state.meetingType.id : ''),
          timeZone: req.timeZone || { id: state.timeZone },
          timeSpan: trimTimeRangeToFromNow(req.timeSpan || state.timeRange),
          userId: req.userId || state.selectedHost,
        });
      }),
      tap((timeSlots) => {
        this.state$$.next({
          ...this.state$$.getValue(),
          timeSlots: timeSlots || [],
          isLoadingTimeSlots: false,
        });
      }),
      switchMapTo(this.timeSlots$)
    );
  }

  setNewMeetingLocation(newLocation: string) {
    this.state$$.next({
      ...this.state$$.getValue(),
      newMeetingLocation: newLocation,
    });
  }

  setTimezone(timeZone: string, isInitialSet: boolean): void {
    this.state$$.next({
      ...this.state$$.getValue(),
      timeZone,
      timeZoneChanged: !isInitialSet,
    });
  }

  setSelectedDate(date: Date): void {
    this.state$$.next({
      ...this.state$$.getValue(),
      selectedDate: date,
    });
  }

  setSelectedHost(selectedHost: string | null): void {
    this.state$$.next({
      ...this.state$$.getValue(),
      selectedHost,
    });
  }

  getSelectedHost(): string {
    return this.state$$.getValue().selectedHost || '';
  }
  setSelectedTimeSlot(timeSlot: TimeSlot): void {
    this.state$$.next({
      ...this.state$$.getValue(),
      selectedTimeSlot: timeSlot,
      isTimeSlotChanged: true,
    });
  }

  setLocation(location: string): void {
    const isLocationChanged =
      location.trim() !== this.state$$.getValue().meetingLocation.trim();
    this.state$$.next({
      ...this.state$$.getValue(),
      meetingLocation: location,
      isLocationChanged,
    });
  }

  isLocationModified(location: string): boolean {
    return location.trim() !== this.state$$.getValue().meetingLocation.trim();
  }

  setSelectedTimeRange(timeRange: Pick<TimeSlot, 'start' | 'end'>): void {
    this.state$$.next({ ...this.state$$.getValue(), timeRange });
  }

  setErrorDisplay(errorDisplay: Errors): void {
    this.state$$.next({
      ...this.state$$.getValue(),
      errorDisplay: errorDisplay || undefined,
    });
  }

  setCompletedActionMessage(message: string): void {
    this.state$$.next({
      ...this.state$$.getValue(),
      completedMeetingMessage: message,
    });
  }

  setMeetingToRescheduleOrCancel(meetingId: string): void {
    this.state$$.next({
      ...this.state$$.getValue(),
      meetingToRescheduleOrCancelId: meetingId,
    });
  }

  setIsTimeSlotChanged(value: boolean): void {
    this.state$$.next({
      ...this.state$$.getValue(),
      isTimeSlotChanged: value,
    });
  }

  setOriginalMeetingTimeSlot(timeSlot: TimeSlot): void {
    this.state$$.next({
      ...this.state$$.getValue(),
      originalMeetingTimeSlot: timeSlot,
    });
  }

  getOriginalMeetingTimeSlot(): TimeSlot | null {
    const state = this.state$$.getValue();
    if (!state.originalMeetingTimeSlot) {
      return null;
    }
    return {
      ...state.originalMeetingTimeSlot,
      timezone:
        state.originalMeetingTimeSlot.timezone ||
        this.createTimezone(state.originalMeetingTimeSlot.start),
    };
  }

  setGuestBookedMeetingV2(meetingInfo: GuestGetBookedMeetingResponseV2): void {
    this.state$$.next({
      ...this.state$$.getValue(),
      guestBookedMeetingV2: meetingInfo,
    });
  }

  getGuestBookedMeetingV2Info(): GuestGetBookedMeetingResponseV2 | null {
    return this.state$$.getValue().guestBookedMeetingV2;
  }

  getCalendarId(): string {
    return this.state$$.getValue().calendarId;
  }

  isClientSiteMeeting(): boolean {
    return (
      this.state$$.getValue().meetingType.locationType ===
      MeetingLocationType.IN_PERSON_CLIENT_SITE
    );
  }

  isVideoMeetingDisabled(): boolean {
    return this.state$$.getValue().meetingType.isVideoLinkDisabled;
  }

  getTimeSlotChanged(): boolean {
    return this.state$$.getValue().isTimeSlotChanged;
  }

  getLocationChanged(): boolean {
    return this.state$$.getValue().isLocationChanged;
  }

  getLocationGuideLines(): string {
    return this.state$$.getValue().meetingType.locationGuidelines;
  }

  getTimezoneChanged(): boolean {
    return this.state$$.getValue().timeZoneChanged;
  }

  getSelectedTimeSlot(): TimeSlot | null {
    const state = this.state$$.getValue();
    if (!state.selectedTimeSlot) {
      return null;
    }
    return {
      ...state.selectedTimeSlot,
      timezone:
        state.selectedTimeSlot.timezone ||
        this.createTimezone(state.selectedTimeSlot.start),
    };
  }

  getTimezone(): string {
    return this.state$$.getValue().timeZone;
  }

  getHostLocation(): string {
    return this.state$$.getValue().meetingType.location;
  }

  getMeetingTypeId(): string {
    return this.state$$.getValue().meetingType
      ? this.state$$.getValue().meetingType.id || ''
      : '';
  }

  bookMeeting(req: {
    calendarId: string;
    meetingTypeId: string;
    start: Date;
    comment: string;
    attendees: Contact[];
    formAnswers: MeetingForm;
    metadata?: {
      [key: string]: string;
    };
    recaptchaToken: string;
    bookingSource?: BookingSource;
    hasSmsConsent?: boolean;
  }): Observable<string> {
    return this.guestService
      .bookMeeting({
        ...req,
        hostId: req.calendarId,
        attendees: req.attendees,
        userId: this.getSelectedHost(),
        bookingSource:
          req.bookingSource || resolveBookingSource(),
        uniqueRequestId: getOrCreateUniqueRequestId(req.meetingTypeId),
      })
      .pipe(
        switchMap((resp) => {
          return this.guestService
            .getHost({ hostId: resp.hostId || req.calendarId })
            .pipe(
              tap((host) =>
                this.state$$.next({
                  ...this.state$$.getValue(),
                  completedMeetingAction: 'book',
                  completedMeetingMessage: $localize`:@@BOOK.CALENDAR_INVITE:A calendar invite has been sent to your email address.`,
                  completedMeetingHost: host,
                })
              ),
              map(() => resp.meetingId)
            );
        })
      );
  }

  bookBatchMeeting(req: {
    targets: BatchBookingTargetInterface[];
    start: Date;
    attendees: Contact[];
    comment?: string;
    metadata?: { [key: string]: string };
    formAnswers?: MeetingForm;
    recaptchaToken: string;
    location?: string;
    bookingSource?: BookingSource;
    bookingOriginId?: string;
  }): Observable<string> {
    const bookingOriginId =
      req.bookingOriginId || this._bookingOriginId || undefined;
    return this.guestService
      .bookBatchMeeting({
        targets: req.targets,
        start: req.start,
        attendees: req.attendees,
        comment: req.comment,
        metadata: req.metadata,
        formAnswers: req.formAnswers,
        recaptchaToken: req.recaptchaToken,
        location: req.location,
        bookingSource:
          req.bookingSource || resolveBookingSource(),
        bookingOriginId,
        uniqueRequestId: getOrCreateUniqueRequestId(bookingOriginId),
      })
      .pipe(
        tap(() => clearUniqueRequestId(bookingOriginId)),
        map((resp) => resp.bookingGroupId)
      );
  }

  rescheduleMeeting(req: {
    authToken?: string;
    meetingId?: string;
    start: Date;
    timeZone?: TimeZone;
    location?: string;
  }): Observable<null> {
    return this.state$.pipe(
      filter<BookMeNowState>((state) => {
        return [
          req.timeZone || state.timeZone,
          req.authToken || state.calendarId,
          req.meetingId || state.meetingToRescheduleOrCancelId,
        ].every(Boolean);
      }),
      first(),
      switchMap((state) => {
        const meetingId = req.meetingId || state.meetingToRescheduleOrCancelId;
        const isBatch = meetingId.startsWith('BG-');

        if (isBatch) {
          return this.rescheduleBatchMeeting(meetingId, req, state);
        }

        const authToken = req.authToken || state.calendarId;
        return this.guestService
          .rescheduleMeeting({
            ...req,
            meetingId,
            timeZone: req.timeZone || { id: state.timeZone },
            authToken,
            location: req.location || state.newMeetingLocation,
            bookingSource: resolveBookingSource(),
          })
          .pipe(
            tap(() => {
              // Don't fetch from backend - it may return stale data
              // Instead, keep the selectedTimeSlot that was already set (with correct duration)
              // Just update the action flags
              const currentState = this.state$$.getValue();
              this.state$$.next({
                ...currentState,
                completedMeetingAction: 'reschedule',
                completedMeetingMessage: $localize`:@@RESCHEDULE.MEETING_RESCHEDULED:Your meeting has been rescheduled`,
              });
            }),
            map(() => null)
          );
      })
    );
  }

  private rescheduleBatchMeeting(
    meetingId: string,
    req: { start: Date; timeZone?: TimeZone; location?: string },
    state: BookMeNowState
  ): Observable<null> {
    return this.guestService
      .rescheduleBatchMeeting({
        id: meetingId,
        start: req.start,
        timeZone: req.timeZone || { id: state.timeZone },
        location: req.location || state.newMeetingLocation,
        bookingSource: resolveBookingSource(),
      })
      .pipe(
        tap(() => {
          this.state$$.next({
            ...this.state$$.getValue(),
            completedMeetingAction: 'reschedule',
            completedMeetingMessage: $localize`:@@RESCHEDULE.MEETINGS_RESCHEDULED:Your meetings have been rescheduled`,
          });
        })
      );
  }

  cancelMeeting(req: {
    authToken?: string;
    meetingId: string;
    cancellationReason: string;
  }): Observable<null> {
    // Detect batch booking (BG- prefix) and route to CancelBatchMeeting API
    const isBatch = req.meetingId.startsWith('BG-');

    if (isBatch) {
      return this.guestService
        .cancelBatchMeeting({
          id: req.meetingId,
          cancellationReason: req.cancellationReason,
          bookingSource: resolveBookingSource(),
        })
        .pipe(
          map(() => null),
          tap(() =>
            this.state$$.next({
              ...this.state$$.getValue(),
              completedMeetingAction: 'cancel',
              completedMeetingMessage: $localize`:@@CANCEL.MEETINGS_CANCELED:Your meetings have been canceled`,
            })
          )
        );
    }

    return this.state$
      .pipe(
        filter<BookMeNowState>((state) => {
          return [req.authToken || state.calendarId].every(Boolean);
        }),
        first(),
        switchMap((state) => {
          return this.guestService.cancelMeeting({
            ...req,
            authToken: req.authToken || state.calendarId,
            bookingSource: resolveBookingSource(),
          });
        })
      )
      .pipe(
        tap(() =>
          this.state$$.next({
            ...this.state$$.getValue(),
            completedMeetingAction: 'cancel',
            completedMeetingMessage: $localize`:@@CANCEL.MEETING_CANCELED:Your meeting has been canceled`,
          })
        )
      );
  }

  getBookedMeetingInfo(
    meetingId: string,
    authToken?: string
  ): Observable<BookedMeetingInfo> {
    return this.state$.pipe(
      filter<BookMeNowState>((state) => Boolean(authToken || state.calendarId)),
      first(),
      switchMap((state) =>
        this.guestService.getBookedMeetingInfo(
          meetingId,
          authToken || state.calendarId
        )
      ),
      tap((meeting) => {
        this.state$$.next({
          ...this.state$$.getValue(),
          selectedTimeSlot: { start: meeting.start, end: meeting.end },
          meetingLocation: meeting.location,
          newMeetingLocation: meeting.location,
          locationGuideLine: meeting.locationGuideline,
          locationType: meeting.locationType,
          displayName: meeting.displayName,
        });
      })
    );
  }

  createTimezone(time: Date): TimezoneRegionAndZone {
    const timezoneRegion = this.getTimezone();
    const timezoneOffset = time.getTimezoneOffset();

    return {
      region: timezoneRegion,
      zone: getZoneFromTimezone(timezoneRegion, timezoneOffset),
    };
  }

  private runningOnServer(): boolean {
    return !isPlatformBrowser(this.platformId);
  }

  storeInitialPath(path: string): void {
    if (!sessionStorage.getItem(this.initialPathKey)) {
      sessionStorage.setItem(this.initialPathKey, path);
    }
  }

  // Retrieve stored path
  getInitialPath(): string | null {
    return sessionStorage.getItem(this.initialPathKey);
  }

  getSessionMeetingInfo(req: {
    sessionMeetingId: string;
    slug: string;
  }): Observable<unknown> {
    const response = this.guestService.getSessionMeetingInfo(req);

    response.subscribe((data: { businessLogoUrl?: string }) => {
      if (data && data.businessLogoUrl) {
        this._businessDisplayLogoUrl.next(data.businessLogoUrl);
      }
    });

    return response;
  }

  inviteeRegisterSessionMeeting(req: {
    meetingId: string;
    attendee: Contact;
    comment: string;
    formAnswers: MeetingForm;
    recaptchaToken: string;
  }): Observable<string> {
    return this.guestService
      .inviteeRegisterSessionMeeting({
        ...req,
        attendee: req.attendee,
      })
      .pipe(
        tap(() => {
          // Update state on successful registration
          this.state$$.next({
            ...this.state$$.getValue(),
            completedMeetingAction: 'book',
            completedMeetingMessage: $localize`:@@SESSION.REGISTRATION_SUCCESS:You have been successfully registered for this session.`,
          });
        }),
        map(() => 'success')
      );
  }

  getSessionInviteeMeetingInfo(sessionInviteeId: string): Observable<unknown> {
    return this.guestService.getSessionInviteeMeetingInfo({ sessionInviteeId });
  }

  getCurrentState(): BookMeNowState {
    return this.state$$.getValue();
  }

  setState(newState: Partial<BookMeNowState>): void {
    const currentState = this.state$$.getValue();
    this.state$$.next({ ...currentState, ...newState });
  }

  inviteeDeregisterSessionMeeting(req: {
    sessionInviteeId: string;
    cancellationReason: string;
  }): Observable<unknown> {
    return this.guestService.inviteeDeregisterSessionMeeting(req).pipe(
      tap(() => {
        // Update state on successful cancellation
        this.state$$.next({
          ...this.state$$.getValue(),
          completedMeetingAction: 'cancel',
          completedMeetingMessage: $localize`:@@SESSION_CANCEL.CANCELLATION_SUCCESS:You have successfully cancelled your session registration.`,
        });
      })
    );
  }

  checkFeatureFlag(req: {
    fieldType: FeatureFlagFieldType;
    fieldValue: string;
    featureFlagKey: string;
  }): Observable<boolean> {
    return this.guestService
      .checkFeatureFlag({
        fieldType: req.fieldType,
        fieldValue: req.fieldValue,
        featureFlagKey: req.featureFlagKey,
      })
      .pipe(map((response) => response.isEnabled || false));
  }

  private _extractServiceLocation(response: GetServiceV2Response): void {
    const allEvents: ServiceEventType[] = [
      ...(response.eventTypes || []),
      ...(response.groups || []).flatMap((g) => g.eventTypes || []),
    ];
    const uniqueLocations = [
      ...new Set(
        allEvents
          .filter(
            (e) =>
              e.locationType === MeetingLocationType.IN_PERSON_USER_SITE &&
              !!e.location
          )
          .map((e) => e.location.trim())
          .filter((loc) => loc.length > 0)
      ),
    ];
    this._serviceLocation.next(uniqueLocations.join(', '));
  }
}

function ifDaniMarioEnsureCustomFields(meetingType: MeetingType): MeetingType {
  if (
    meetingType &&
    (meetingType.id === 'dmario-inbound' ||
      meetingType.meetingTypeSlug === 'dmario-inbound') &&
    (meetingType.form?.fields || []).length === 0
  ) {
    return { ...meetingType, form: getDaniMarioForm() };
  }
  return meetingType;
}
